import type { FC } from "react";
import type { OrderItemDTO, OrderItemGroupDTO } from "../../../../libs/mypage/orderResolver";
import { formatYen, type OrderDetailDTO } from "../../components/format";
import { SectionHeading } from "../../components/SectionHeading";

export const ItemsSection: FC<{ order: OrderDetailDTO }> = ({ order }) => (
  <section className="mb-10">
    <SectionHeading title="ご注文内容" meta={`${order.itemGroups.length} 点`} />
    <ul className="divide-y divide-neutral-200">
      {order.itemGroups.map((group) =>
        group.kind === "single" ? (
          <SingleItemRow key={group.item.lineItemId} item={group.item} />
        ) : (
          <BundleGroupRow key={group.parent.lineItemId} group={group} />
        ),
      )}
    </ul>
  </section>
);

const SingleItemRow: FC<{ item: OrderItemDTO }> = ({ item }) => (
  <li className="flex items-center gap-4 py-4">
    <img
      src={item.thumbnailUrl}
      alt=""
      width={64}
      height={64}
      className="size-16 flex-shrink-0 rounded border border-neutral-200 bg-neutral-50 object-cover"
    />
    <div className="min-w-0 flex-1">
      <p className="truncate font-medium text-neutral-900 text-sm">{item.title}</p>
      {item.variantTitle && <p className="mt-1 text-neutral-500 text-xs">{item.variantTitle}</p>}
      <p className="mt-1 text-neutral-500 text-xs">数量 {item.quantity}</p>
    </div>
    <p className="flex-shrink-0 text-neutral-700 text-sm">
      {formatYen(item.priceJpy * item.quantity)}
    </p>
  </li>
);

type BundleGroup = Extract<OrderItemGroupDTO, { kind: "bundle" }>;

const BundleGroupRow: FC<{ group: BundleGroup }> = ({ group }) => {
  const { parent, children, subtotalJpy } = group;
  return (
    <li className="py-4">
      <div className="flex items-center gap-4">
        <img
          src={parent.thumbnailUrl}
          alt=""
          width={64}
          height={64}
          className="size-16 flex-shrink-0 rounded border border-neutral-200 bg-neutral-50 object-cover"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-neutral-900 text-sm">{parent.title}</p>
          {parent.variantTitle && (
            <p className="mt-1 text-neutral-500 text-xs">{parent.variantTitle}</p>
          )}
          <p className="mt-1 text-neutral-400 text-xs">セット内訳 {children.length} 点</p>
        </div>
        <p className="flex-shrink-0 text-neutral-700 text-sm">{formatYen(subtotalJpy)}</p>
      </div>
      <ul className="mt-3 ml-2 space-y-2 border-neutral-200 border-l pl-3 sm:ml-20 sm:pl-4">
        {children.map((child) => (
          <li key={child.lineItemId} className="flex items-center gap-3 text-neutral-600 text-xs">
            <img
              src={child.thumbnailUrl}
              alt=""
              width={32}
              height={32}
              className="size-8 flex-shrink-0 rounded border border-neutral-200 bg-neutral-50 object-cover"
            />
            <span className="min-w-0 flex-1 truncate">
              {child.title}
              {child.variantTitle ? ` ・ ${child.variantTitle}` : ""}
            </span>
            <span className="flex-shrink-0 text-neutral-500">× {child.quantity}</span>
          </li>
        ))}
      </ul>
    </li>
  );
};
